import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Download, Save, Minimize2, Maximize2, Settings, Zap, BookOpen, Brain, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import deepseekChatAPI from '../services/deepseekChatApi';
import Toast from './Toast';

const DeepSeekChatWindow = ({ 
  isOpen, 
  onClose, 
  contextData = null, // Current HKDSE text context
  initialMode = 'direct' // 'quiz', 'drill', 'direct'
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatMode, setChatMode] = useState(initialMode);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected', 'error'
  const [toast, setToast] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        type: 'assistant',
        content: getWelcomeMessage(),
        timestamp: new Date().toISOString()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, chatMode, contextData]);

  const getWelcomeMessage = () => {
    const contextInfo = contextData ? `（當前學習：${contextData.title}）` : '';
    
    switch (chatMode) {
      case 'quiz':
        return `🎯 **測驗生成模式** ${contextInfo}\n\n我可以為你生成各種類型的測驗題目：\n• 選擇題\n• 填空題\n• 問答題\n• 分析題\n\n請告訴我你想要什麼類型的題目！\n\n💡 提示：你可以說「為《魚我所欲也》生成5道選擇題」`;
      case 'drill':
        return `📚 **練習模式** ${contextInfo}\n\n我可以幫你進行各種學習練習：\n• 重點複習\n• 概念解釋\n• 例題演練\n• 知識鞏固\n\n讓我們開始學習吧！\n\n💡 提示：你可以說「解釋《師說》的主要論點」`;
      default:
        return `💬 **DeepSeek AI 智能助手** ${contextInfo}\n\n你好！我是專門協助HKDSE中文學習的AI助手。我可以幫助你：\n\n📖 **文本理解**\n• 解釋範文內容和主旨\n• 分析修辭手法和寫作技巧\n• 回答考試重點問題\n\n✏️ **學習指導**\n• 提供學習建議和複習方法\n• 解答疑難問題\n• 協助備考準備\n\n🎯 **個人化支援**\n• 根據你的學習進度調整內容\n• 提供即時反饋和建議\n\n有什麼我可以幫助你的嗎？😊`;
    }
  };

  // Get a simplified welcome message for PDF export
  const getSimpleWelcomeMessage = () => {
    const contextInfo = contextData ? `（${contextData.title}）` : '';
    return `DeepSeek AI 智能助手 ${contextInfo}\n\n有什麼我可以幫助你的嗎？`;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setConnectionStatus('connecting');

    try {
      const response = await deepseekChatAPI.sendMessage(inputMessage, {
        type: chatMode,
        textId: contextData?.id,
        textTitle: contextData?.title,
        category: contextData?.category,
        currentContent: contextData?.content
      });

      if (response.success) {
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          metadata: response.context
        };

        setMessages(prev => [...prev, assistantMessage]);
        
        // Update chat history
        setChatHistory(prev => [...prev, userMessage, assistantMessage]);
        setConnectionStatus('connected');
        if (connectionStatus !== 'connected') { // Only show toast on first successful connection
          setToast({ message: 'DeepSeek AI 連接成功！', type: 'success' });
        }
      } else {
        setConnectionStatus('error');
        throw new Error(response.error || 'API request failed');
      }
    } catch (error) {
      console.error('DeepSeek Chat Error:', error);
      setConnectionStatus('error');
      setToast({ message: 'AI連接失敗，請檢查網路連接', type: 'error' });
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `抱歉，發生了錯誤：${error.message}。請稍後再試。`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleExportPDF = async () => {
    try {
      // Create a temporary container for the chat content with optimized spacing
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.padding = '15mm'; // Reduced padding for more content space
      tempContainer.style.fontFamily = 'Arial, "Microsoft YaHei", "SimHei", "SimSun", sans-serif';
      tempContainer.style.fontSize = '11px'; // Slightly smaller font for more content
      tempContainer.style.lineHeight = '1.5'; // Tighter line height
      tempContainer.style.color = '#333';
      tempContainer.style.backgroundColor = 'white';

      // Get current date and time
      const now = new Date();
      const dateTime = now.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      // Get context information
      const contextInfo = contextData ? `學習內容：${contextData.title}` : '';
      const modeLabel = chatMode === 'quiz' ? '測驗生成模式' : 
                       chatMode === 'drill' ? '練習模式' : '智能對話模式';

      // Create the HTML content with optimized layout
      tempContainer.innerHTML = `
        <div style="max-width: 170mm; margin: 0 auto;">
          <!-- Compact Header -->
          <div style="text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #4F46E5;">
            <h1 style="font-size: 18px; font-weight: bold; margin: 0 0 5px 0; color: #4F46E5;">
              DeepSeek AI 學習對話記錄
            </h1>
            <div style="font-size: 12px; color: #666; margin-bottom: 3px;">
              ${modeLabel} ${contextInfo ? `• ${contextInfo}` : ''}
            </div>
            <div style="font-size: 10px; color: #888;">
              導出時間：${dateTime}
            </div>
          </div>

          <!-- Messages with optimized spacing -->
          <div style="space-y: 15px;">
            ${messages.map(message => {
              // Skip the default welcome message completely in PDF
              if (message.type === 'assistant' && message.content.includes('DeepSeek AI 智能助手') && message.content.includes('我可以幫助你')) {
                return ''; // Don't include the welcome message at all
              } else if (message.type === 'assistant') {
                return `
                  <div style="margin-bottom: 15px; padding: 12px; background-color: #F8FAFC; border-left: 4px solid #4F46E5; border-radius: 6px;">
                    <div style="font-weight: bold; color: #4F46E5; margin-bottom: 6px; font-size: 13px;">
                      🤖 DeepSeek AI 老師
                    </div>
                    <div style="color: #374151; white-space: pre-wrap; word-wrap: break-word; font-size: 12px;">
                      ${message.content}
                    </div>
                    <div style="font-size: 9px; color: #9CA3AF; margin-top: 6px;">
                      ${new Date(message.timestamp).toLocaleString('zh-TW')}
                    </div>
                  </div>
                `;
              } else if (message.type === 'user') {
                return `
                  <div style="margin-bottom: 15px; padding: 12px; background-color: #EFF6FF; border-left: 4px solid #3B82F6; border-radius: 6px;">
                    <div style="font-weight: bold; color: #3B82F6; margin-bottom: 6px; font-size: 13px;">
                      👤 學生
                    </div>
                    <div style="color: #374151; white-space: pre-wrap; word-wrap: break-word; font-size: 12px;">
                      ${message.content}
                    </div>
                    <div style="font-size: 9px; color: #9CA3AF; margin-top: 6px;">
                      ${new Date(message.timestamp).toLocaleString('zh-TW')}
                    </div>
                  </div>
                `;
              } else if (message.type === 'error') {
                return `
                  <div style="margin-bottom: 15px; padding: 12px; background-color: #FEF2F2; border-left: 4px solid #EF4444; border-radius: 6px;">
                    <div style="font-weight: bold; color: #EF4444; margin-bottom: 6px; font-size: 13px;">
                      ⚠️ 系統錯誤
                    </div>
                    <div style="color: #374151; white-space: pre-wrap; word-wrap: break-word; font-size: 12px;">
                      ${message.content}
                    </div>
                    <div style="font-size: 9px; color: #9CA3AF; margin-top: 6px;">
                      ${new Date(message.timestamp).toLocaleString('zh-TW')}
                    </div>
                  </div>
                `;
              }
              return '';
            }).join('')}
          </div>

          <!-- Compact Footer -->
          <div style="margin-top: 25px; text-align: center; font-size: 9px; color: #9CA3AF; border-top: 1px solid #E5E7EB; padding-top: 10px;">
            <div>由 RightChinese • DeepSeek AI 學習系統生成</div>
            <div>總計 ${messages.length} 條對話記錄</div>
          </div>
        </div>
      `;

      // Add to document temporarily
      document.body.appendChild(tempContainer);

      // Wait for fonts to load
      await new Promise(resolve => setTimeout(resolve, 200));

      // Convert to canvas with high quality
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: 'white',
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight,
        logging: false,
        imageTimeout: 0
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Add image to PDF, handle multiple pages if needed
      let position = 0;
      const pageHeight = pdfHeight;

      while (position < imgHeight) {
        if (position > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight);
        position += pageHeight;
      }

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const contextPart = contextData ? `_${contextData.title.replace(/[^\w\s]/gi, '')}` : '';
      const fileName = `DeepSeek對話記錄_${modeLabel}${contextPart}_${timestamp}.pdf`;

      // Save the PDF
      pdf.save(fileName);

      // Show success toast
      setToast({ message: 'PDF 對話記錄已成功下載！', type: 'success' });

    } catch (error) {
      console.error('PDF Export failed:', error);
      setToast({ message: 'PDF 導出失敗，請稍後再試', type: 'error' });
    }
  };

  const clearChat = () => {
    setMessages([]);
    setChatHistory([]);
    // Re-add welcome message
    const welcomeMessage = {
      id: Date.now(),
      type: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
  };

  const chatModes = [
    { id: 'direct', label: '直接對話', icon: MessageCircle, color: 'bg-blue-500' },
    { id: 'quiz', label: '測驗生成', icon: Zap, color: 'bg-purple-500' },
    { id: 'drill', label: '練習模式', icon: BookOpen, color: 'bg-green-500' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`bg-white rounded-2xl shadow-2xl transition-all duration-300 ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-800">DeepSeek AI</h3>
                <div className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500' :
                  connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                  connectionStatus === 'error' ? 'bg-red-500' : 'bg-gray-400'
                }`}></div>
              </div>
              <p className="text-xs text-gray-500">
                智能學習助手 • {
                  connectionStatus === 'connected' ? '已連接' :
                  connectionStatus === 'connecting' ? '連接中...' :
                  connectionStatus === 'error' ? '連接錯誤' : '未連接'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={handleExportPDF}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              title="匯出 PDF 對話記錄"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={clearChat}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              title="清除對話記錄"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Settings Panel */}
            {showSettings && (
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    對話模式
                  </label>
                  <div className="flex gap-2">
                    {chatModes.map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => {
                          setChatMode(mode.id);
                          setShowSettings(false);
                          clearChat();
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          chatMode === mode.id
                            ? `${mode.color} text-white`
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <mode.icon className="w-4 h-4" />
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={async () => {
                      setConnectionStatus('connecting');
                      const isConnected = await deepseekChatAPI.testConnection();
                      setConnectionStatus(isConnected ? 'connected' : 'error');
                      setToast({ 
                        message: isConnected ? 'API連接測試成功！' : 'API連接測試失敗', 
                        type: isConnected ? 'success' : 'error' 
                      });
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                  >
                    <Zap className="w-4 h-4" />
                    測試連接
                  </button>
                  <button
                    onClick={clearChat}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    <RotateCcw className="w-4 h-4" />
                    清除對話
                  </button>
                </div>
              </div>
            )}

            {/* Context Info */}
            {contextData && (
              <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm">
                  <BookOpen className="w-4 h-4 text-indigo-600" />
                  <span className="text-gray-700">當前學習：</span>
                  <span className="font-medium text-indigo-700">{contextData.title}</span>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ height: 'calc(100% - 200px)' }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                        : message.type === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm">
                      {message.content}
                    </div>
                    <div className={`text-xs mt-1 ${
                      message.type === 'user' ? 'text-indigo-100' : 'text-gray-500'
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 p-3 rounded-2xl">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <span className="text-sm text-gray-600 ml-2">AI思考中...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex gap-2">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`輸入你的${chatMode === 'quiz' ? '測驗需求' : chatMode === 'drill' ? '學習問題' : '問題'}...`}
                  className="flex-1 p-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  rows="2"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  發送
                </button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <div className={`w-2 h-2 rounded-full ${
                    chatMode === 'quiz' ? 'bg-purple-500' : 
                    chatMode === 'drill' ? 'bg-green-500' : 'bg-blue-500'
                  }`}></div>
                  {chatModes.find(m => m.id === chatMode)?.label}
                </div>
                <div className="text-xs text-gray-400">
                  Enter發送 • Shift+Enter換行
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default DeepSeekChatWindow; 